import React, { useState, useEffect } from "react";
import { Plus, LogIn, Crown, Music2, QrCode, Sparkles, Trash2 } from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import { usePartyStore } from "../../stores/partyStore";
import { 
  db, 
  collection, 
  doc,
  query, 
  where, 
  getDocs, 
  setDoc,
  deleteDoc,
  createParty,
  joinParty,
  linkAnonymousWithGoogle 
} from "../../lib/firebase";

export const LobbyView: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const { setParty, setUserRole } = usePartyStore();

  const [joinCode, setJoinCode] = useState("");
  const [partyName, setPartyName] = useState("");
  const [hostedParties, setHostedParties] = useState<any[]>([]);
  const [loadingJoin, setLoadingJoin] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check URL query parameters for ?partyCode=
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const codeFromUrl = urlParams.get("partyCode");
    if (codeFromUrl && user) {
      setJoinCode(codeFromUrl.toUpperCase());
      handleJoinWithCode(codeFromUrl.toUpperCase());
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [user]);

  // Load hosted parties for this user
  useEffect(() => {
    if (!user || user.isAnonymous) return;

    const fetchHostedParties = async () => {
      try {
        const q = query(
          collection(db, "parties"),
          where("hostId", "==", user.uid)
        );
        const snap = await getDocs(q);
        const parties = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
        setHostedParties(parties);
      } catch (err) {
        console.error("Error fetching hosted parties:", err);
      }
    };

    fetchHostedParties();
  }, [user]);

  const handleJoinWithCode = async (codeToJoin: string) => {
    if (!user) return;
    const cleanCode = codeToJoin.trim().toUpperCase();
    if (cleanCode.length !== 6) {
      setError("Party code must be exactly 6 characters.");
      return;
    }

    setLoadingJoin(true);
    setError(null);

    try {
      const result = await joinParty(cleanCode, user);
      setUser({ ...user, currentPartyId: result.partyId });
      setParty({
        id: result.partyId,
        partyName: result.partyName,
        partyCode: result.partyCode,
        hostId: result.hostId,
        hostName: result.hostName,
        status: "active",
        createdAt: result.createdAt,
      });
      setUserRole("GUEST");
    } catch (err: any) {
      setError(err.message || "Failed to join party.");
    } finally {
      setLoadingJoin(false);
    }
  };

  const handleResumeHostedParty = async (party: any) => {
    if (!user) return;
    setLoadingJoin(true);
    setError(null);

    try {
      // 1. Ensure user document has currentPartyId
      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, { currentPartyId: party.id }, { merge: true });

      // 2. Ensure member record exists as HOST
      const memberRef = doc(db, "parties", party.id, "members", user.uid);
      await setDoc(memberRef, {
        name: user.displayName || "Host",
        photoURL: user.photoURL || "",
        role: "HOST",
        uid: user.uid,
        isAnonymous: user.isAnonymous ?? false,
        joinedAt: Date.now(),
      }, { merge: true });

      // 3. Ensure queueOrder/main exists
      const queueOrderRef = doc(db, "parties", party.id, "queueOrder", "main");
      await setDoc(queueOrderRef, { videoIds: party.queueOrder || [] }, { merge: true });

      setUser({ ...user, currentPartyId: party.id });
      setParty({
        id: party.id,
        partyName: party.partyName || "Karaoke Party",
        partyCode: party.partyCode || party.id,
        hostId: user.uid,
        hostName: user.displayName,
        status: "active",
        createdAt: party.createdAt || new Date().toISOString(),
        isPlaying: true,
      });
      setUserRole("HOST");
    } catch (err: any) {
      console.error("Error resuming party:", err);
      setError(err.message || "Could not resume party room.");
    } finally {
      setLoadingJoin(false);
    }
  };

  const handleDeleteHostedParty = async (partyId: string, partyName: string) => {
    const confirmed = window.confirm(`Are you sure you want to permanently delete "${partyName}"?`);
    if (!confirmed) return;

    setDeletingId(partyId);
    try {
      // Delete party document directly from Firestore
      await deleteDoc(doc(db, "parties", partyId));
      setHostedParties((prev) => prev.filter((p) => p.id !== partyId));
    } catch (err: any) {
      console.error("Error deleting party:", err);
      alert(`Could not delete party: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleJoinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleJoinWithCode(joinCode);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!partyName.trim()) {
      setError("Please choose a party name!");
      return;
    }

    setLoadingCreate(true);
    setError(null);

    try {
      const result = await createParty(partyName.trim(), user);

      setUser({ ...user, currentPartyId: result.partyId });
      setParty({
        id: result.partyId,
        partyName: result.partyName,
        partyCode: result.partyCode,
        hostId: user.uid,
        hostName: user.displayName,
        status: "active",
        createdAt: result.createdAt,
        isPlaying: true,
      });
      setUserRole("HOST");
    } catch (err: any) {
      console.error("Error creating party:", err);
      setError(err.message || "Failed to create party. Please try again.");
    } finally {
      setLoadingCreate(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-8 animate-fade-in">
      {/* Welcome Banner */}
      <div className="bg-card p-6 sm:p-8 rounded-3xl border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xl">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-text-main">
              Welcome, {user?.firstName}!
            </h2>
            {user?.isAnonymous && (
              <span className="bg-accent/20 text-accent text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
                Guest
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm text-text-muted">
            Join your friends&apos; karaoke room or start a new party as host.
          </p>
        </div>

        {user?.isAnonymous && (
          <button
            onClick={() => linkAnonymousWithGoogle()}
            className="self-start sm:self-auto py-2.5 px-4 rounded-2xl bg-accent/10 border border-accent/30 hover:border-accent text-accent text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
          >
            <Sparkles className="w-4 h-4" />
            <span>Link Google to Host</span>
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 text-xs sm:text-sm bg-red-500/10 border border-red-500/30 text-red-500 rounded-2xl font-medium">
          {error}
        </div>
      )}

      {/* Main Panels Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Join Party Panel */}
        <div className="bg-card p-6 rounded-3xl border border-border shadow-xl space-y-6 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-accent/10 border border-accent/30 flex items-center justify-center text-accent">
              <LogIn className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-bold font-heading text-text-main">Join a Party</h3>
              <p className="text-xs text-text-muted">
                Scan the QR code on the TV or enter the 6-character room code below:
              </p>
            </div>
          </div>

          <form onSubmit={handleJoinSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={6}
                required
                className="w-full text-center tracking-[0.3em] font-mono text-2xl font-bold py-3.5 bg-background border border-border focus:border-accent rounded-2xl text-accent placeholder-text-muted/40 outline-none uppercase transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loadingJoin || joinCode.length !== 6}
              className="w-full py-3.5 px-6 rounded-2xl bg-accent hover:bg-accent-hover text-white font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-accent/25 cursor-pointer disabled:opacity-50"
            >
              <LogIn className="w-4 h-4" />
              <span>{loadingJoin ? "Connecting..." : "Join Party"}</span>
            </button>
          </form>
        </div>

        {/* Create Party Panel */}
        <div className="bg-card p-6 rounded-3xl border border-border shadow-xl space-y-6 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary">
              <Crown className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-bold font-heading text-text-main">Create a Party</h3>
              <p className="text-xs text-text-muted">
                You will be the party host, control the TV playback screen, and manage the queue.
              </p>
            </div>
          </div>

          <form onSubmit={handleCreateSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                value={partyName}
                onChange={(e) => setPartyName(e.target.value)}
                placeholder="e.g. Friday Karaoke Night"
                maxLength={35}
                required
                className="w-full py-3.5 px-4 bg-background border border-border focus:border-primary rounded-2xl text-text-main placeholder-text-muted text-sm outline-none transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loadingCreate || !partyName.trim()}
              className="w-full py-3.5 px-6 rounded-2xl bg-primary hover:bg-primary-hover text-white font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-primary/25 cursor-pointer disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              <span>{loadingCreate ? "Creating..." : "Start Party as Host"}</span>
            </button>
          </form>
        </div>
      </div>

      {/* Hosted Parties Section */}
      {hostedParties.length > 0 && (
        <div className="space-y-4 pt-4">
          <h3 className="text-xl font-bold font-heading text-text-main flex items-center gap-2">
            <Music2 className="w-5 h-5 text-primary" />
            Your Hosted Rooms
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {hostedParties.map((party) => (
              <div
                key={party.id}
                className="p-4 rounded-2xl bg-card border border-border hover:border-primary transition-all flex items-center justify-between group shadow-md"
              >
                <div
                  onClick={() => handleResumeHostedParty(party)}
                  className="min-w-0 flex-1 cursor-pointer pr-2"
                  title="Resume party as Host"
                >
                  <h4 className="text-sm font-bold text-text-main group-hover:text-primary transition-colors truncate">
                    {party.partyName}
                  </h4>
                  <p className="text-xs text-text-muted font-mono tracking-wider">
                    Code: {party.partyCode || party.id}
                  </p>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleResumeHostedParty(party)}
                    className="p-2 rounded-xl bg-background text-text-muted group-hover:text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                    title="Enter Party Room"
                  >
                    <QrCode className="w-4 h-4" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteHostedParty(party.id, party.partyName || "this party");
                    }}
                    disabled={deletingId === party.id}
                    className="p-2 rounded-xl text-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer disabled:opacity-50"
                    title="Delete Party"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
